import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { can } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, Plus, Loader2, Save, X, Undo2 } from "lucide-react";

/**
 * Shared pieces of the manual email templates (Notification Setup → General Email
 * Templates): saved To/CC/BCC recipients, the From identity and the preview header.
 *
 * Recipients are a notification_configs doc per template (action_type), saved via
 * the normal POST/PUT /notification-config/admin endpoints. Every send goes to the
 * SAVED lists (backend/services/manual_email_service.py), so `sendBlocker` stops a
 * send while edits are unsaved — the preview never shows a list the email skips.
 */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * A readable message from an axios error, incl. FastAPI 422 detail arrays.
 * `itemLabels` turns list indexes into names, e.g. { shipments: "AWB" } renders
 * ["body","shipments",0,"awb"] as "AWB #1 › awb".
 */
export function apiErrorMessage(err, fallback, itemLabels = {}) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = (d.loc || []).filter((p) => p !== "body");
        const where = loc
          .map((p, i) => (typeof p === "number" && itemLabels[loc[i - 1]] ? `${itemLabels[loc[i - 1]]} #${p + 1}` : p))
          .filter((p) => !itemLabels[p])
          .join(" › ");
        return where ? `${where}: ${d.msg}` : d.msg;
      })
      .join("; ");
  }
  return (typeof detail === "string" && detail) || err?.response?.data?.error || fallback;
}

function RecipientList({ id, label, hint, emails, onChange, color, disabled }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(null);

  const add = () => {
    const email = input.trim();
    if (!EMAIL_RE.test(email)) { setError("Enter a valid email address"); return; }
    if (emails.some((e) => e.toLowerCase() === email.toLowerCase())) { setError("Already in this list"); return; }
    onChange([...emails, email]);
    setInput("");
    setError(null);
  };

  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      <div className="flex gap-2 mt-1">
        <Input id={id} type="email" placeholder={`Add ${label} email`} value={input} disabled={disabled}
          aria-invalid={Boolean(error)}
          className={error ? "border-red-500" : ""}
          onChange={(e) => { setInput(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${id}-input`} />
        <Button type="button" variant="outline" onClick={add} disabled={disabled}
          className="shrink-0" aria-label={`Add ${label} recipient`} data-testid={`${id}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {emails.map((em) => (
            <Badge key={em} variant="secondary" className={`${color} text-xs max-w-full`}>
              <Mail className="w-3 h-3 mr-1 shrink-0" /><span className="break-all">{em}</span>
              {!disabled && (
                <button type="button" onClick={() => onChange(emails.filter((e) => e !== em))}
                  className="ml-1 hover:text-red-600" aria-label={`Remove ${em}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Saved-recipient state for one template. `senderPath` is the template's
 * GET …/sender endpoint (relative to /notification-config/admin).
 */
export function useTemplateRecipients({ actionType, senderPath, user, configs, onConfigSaved }) {
  const canManage = can(user, "notifications:manage");
  const saved = configs.find((c) => c.action_type === actionType);
  const savedTo = saved?.recipient_emails || [];
  const savedCc = saved?.cc_emails || [];
  const savedBcc = saved?.bcc_emails || [];
  // Re-sync the editable lists only when the SAVED lists actually change, so a
  // refetch triggered elsewhere on the page never wipes in-progress edits.
  const savedKey = JSON.stringify([savedTo, savedCc, savedBcc]);

  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sender, setSender] = useState(undefined);

  useEffect(() => {
    const [t, c, b] = JSON.parse(savedKey);
    setTo(t); setCc(c); setBcc(b);
  }, [savedKey]);

  useEffect(() => {
    axios.get(`${API}/notification-config/admin/${senderPath}`)
      .then((res) => setSender(res.data))
      .catch(() => setSender(null));
  }, [senderPath]);

  const dirty = !sameList(to, savedTo) || !sameList(cc, savedCc) || !sameList(bcc, savedBcc);

  // Why Trigger Now cannot send right now (null = it can).
  const sendBlocker = !canManage ? "You need permission to manage notifications to send this email."
    : !saved ? "Save the recipients before sending."
      : dirty ? "Save or discard your recipient changes before sending."
        : null;

  const save = async () => {
    if (!to.length) { toast.error("Add at least one To recipient"); return; }
    setSaving(true);
    const body = { recipient_emails: to, cc_emails: cc, bcc_emails: bcc };
    try {
      if (saved) {
        await axios.put(`${API}/notification-config/admin/${actionType}`, body);
      } else {
        await axios.post(`${API}/notification-config/admin`, { action_type: actionType, is_enabled: true, ...body });
      }
      toast.success("Recipients saved");
      await onConfigSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save recipients"));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setTo(savedTo); setCc(savedCc); setBcc(savedBcc); };

  return { canManage, saved, to, cc, bcc, setTo, setCc, setBcc, dirty, saving, save, discard, sender, sendBlocker };
}

/** The Recipients card. `idPrefix` keeps input ids unique per template. */
export function RecipientsCard({ recipients: r, idPrefix, description }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">Recipients</CardTitle>
          {r.dirty && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Unsaved changes</Badge>}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        <RecipientList id={`${idPrefix}-to`} label="To" emails={r.to} onChange={r.setTo}
          color="bg-teal-50 text-teal-700" disabled={!r.canManage} />
        <RecipientList id={`${idPrefix}-cc`} label="CC" emails={r.cc} onChange={r.setCc}
          color="bg-blue-50 text-blue-700" disabled={!r.canManage} />
        <RecipientList id={`${idPrefix}-bcc`} label="BCC" hint="BCC recipients are not visible to anyone else."
          emails={r.bcc} onChange={r.setBcc} color="bg-gray-100 text-gray-700" disabled={!r.canManage} />
        {r.canManage && (
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {r.dirty && r.saved && (
              <Button type="button" variant="outline" onClick={r.discard} disabled={r.saving}>
                <Undo2 className="w-4 h-4 mr-1" />Discard
              </Button>
            )}
            <Button type="button" className="bg-teal-600 hover:bg-teal-700"
              onClick={r.save} disabled={r.saving || (!r.dirty && Boolean(r.saved))}
              data-testid={`${idPrefix}-save-recipients`}>
              {r.saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Recipients
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** From / To / CC / BCC / Subject block at the top of a template's email preview. */
export function EmailPreviewHeader({ sender, to, cc, bcc, subject, subjectTestId }) {
  const fromLine = sender === undefined ? "Loading…"
    : sender?.from_email ? `${sender.from_name} <${sender.from_email}>`
      : "Sender address unavailable (backend EMAIL_USER not set)";
  const list = (xs) => (xs.length ? xs.join(", ") : <span className="text-gray-400">—</span>);

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pb-3 border-b border-gray-200">
      <dt className="text-gray-500">From</dt><dd className="break-all">{fromLine}</dd>
      <dt className="text-gray-500">To</dt><dd className="break-all">{list(to)}</dd>
      <dt className="text-gray-500">CC</dt><dd className="break-all">{list(cc)}</dd>
      <dt className="text-gray-500">BCC</dt>
      <dd className="break-all">{list(bcc)}{bcc.length > 0 && <span className="block text-xs text-gray-400">Hidden from other recipients</span>}</dd>
      <dt className="text-gray-500">Subject</dt>
      <dd className="font-medium break-words" data-testid={subjectTestId}>{subject}</dd>
    </dl>
  );
}
